export const HAR_MAX_ENTRIES = 500;

export interface HarEntrySummary {
  index: number;
  startedDateTime: string;
  method: string;
  url: string;
  status: number | null;
  durationMs: number | null;
  sizeBytes: number | null;
  requestHeaders: Record<string, string>;
  responseHeaders: Record<string, string>;
  requestBody: string | null;
  responseBody: string | null;
}

export interface HarPreview {
  entries: HarEntrySummary[];
  truncated: boolean;
  creator: string | null;
}

export function parseHar(bytes: Uint8Array): HarPreview {
  const raw = new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  const parsed = JSON.parse(raw) as {
    log?: {
      creator?: { name?: string; version?: string };
      entries?: unknown[];
    };
  };
  const entries = Array.isArray(parsed.log?.entries) ? parsed.log!.entries! : [];
  const truncated = entries.length > HAR_MAX_ENTRIES;
  const limited = entries.slice(0, HAR_MAX_ENTRIES);

  return {
    creator: parsed.log?.creator?.name
      ? `${parsed.log.creator.name}${parsed.log.creator.version ? ` ${parsed.log.creator.version}` : ''}`
      : null,
    truncated,
    entries: limited.map((entry, index) => summarizeHarEntry(entry, index)),
  };
}

function summarizeHarEntry(entry: unknown, index: number): HarEntrySummary {
  const e = entry as Record<string, unknown>;
  const request = (e.request ?? {}) as Record<string, unknown>;
  const response = (e.response ?? {}) as Record<string, unknown>;
  const timings = (e.timings ?? {}) as Record<string, unknown>;

  const durationMs = typeof e.time === 'number'
    ? e.time
    : Object.values(timings).reduce<number>((sum, value) => sum + (typeof value === 'number' ? value : 0), 0);

  return {
    index,
    startedDateTime: typeof e.startedDateTime === 'string' ? e.startedDateTime : '',
    method: typeof request.method === 'string' ? request.method : '',
    url: typeof request.url === 'string' ? request.url : '',
    status: typeof response.status === 'number' ? response.status : null,
    durationMs: Number.isFinite(durationMs) ? durationMs : null,
    sizeBytes: typeof response.bodySize === 'number' ? response.bodySize : null,
    requestHeaders: headersToMap(request.headers),
    responseHeaders: headersToMap(response.headers),
    requestBody: readBodyText(request.postData),
    responseBody: readBodyText(response.content),
  };
}

function headersToMap(headers: unknown): Record<string, string> {
  if (!Array.isArray(headers)) return {};
  const out: Record<string, string> = {};
  for (const header of headers) {
    const h = header as { name?: string; value?: string };
    if (!h.name) continue;
    out[h.name] = h.value ?? '';
  }
  return out;
}

function readBodyText(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as { text?: string; mimeType?: string };
  if (typeof v.text !== 'string') return null;
  return v.text.slice(0, 8_000);
}
